import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GripVertical, Pencil, Trash2 } from 'lucide-react';
import type { PlybookPly, PlybookZonePosition } from '@/types/plybook';
import { cn } from '@/lib/utils';

interface PlyCardProps {
  ply: PlybookPly;
  zonePositions?: PlybookZonePosition[];
  onEdit?: () => void;
  onDelete?: () => void;
  isDragging?: boolean;
  dragHandleProps?: any;
}

export function PlyCard({
  ply,
  zonePositions = [],
  onEdit,
  onDelete,
  isDragging,
  dragHandleProps,
}: PlyCardProps) {
  // Get zone color - priority: zone color > ply color > default gray
  const getZoneColor = () => {
    if (ply.zone && zonePositions.length > 0) {
      const zonePos = zonePositions.find(zp => zp.zone?.name === ply.zone);
      if (zonePos?.zone?.color) return zonePos.zone.color;
    }
    return ply.color || '#6b7280';
  };

  const bgColor = getZoneColor();

  return (
    <Card
      className={cn(
        'transition-all',
        isDragging && 'shadow-lg ring-2 ring-primary'
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          {/* Drag handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
          >
            <GripVertical className="w-5 h-5" />
          </div>

          {/* Ply number with ZONE color */}
          <div className="flex items-center gap-2">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold shadow-sm"
              style={{ backgroundColor: bgColor }}
            >
              {ply.ply_number}
            </div>
          </div>

          {/* Ply info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">
                {ply.material?.name || 'Sin material'}
              </span>
              {ply.zone && (
                <Badge variant="outline" className="text-xs">
                  {ply.zone}
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {ply.orientation ? `Orientación: ${ply.orientation}°` : ''}
              {ply.orientation && ply.notes ? ' • ' : ''}
              {ply.notes || ''}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {onEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onEdit}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
